import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import "./CalendarPage.css";

function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [modalEvent, setModalEvent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editData, setEditData] = useState({ scheduleId: "", startDate: "", endDate: "", note: "" });
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  // ================= SHOW NOTIFICATION =================
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // ================= FETCH REQUESTS =================
  const fetchRequests = () => {
    fetch("https://api.kemari-corp.com/getServiceRequests.php")
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") setRequests(data.data);
      })
      .catch(err => console.error("Failed to fetch requests:", err));
  };

  // ================= FETCH SCHEDULES =================
  const fetchSchedules = () => {
    fetch("https://api.kemari-corp.com/getSchedules.php")
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          const mappedEvents = data.data.map(s => ({
            id: s.schedule_id,
            title: `${s.service} - ${s.name}`,
            start: s.start_date,
            end: new Date(new Date(s.end_date).getTime() + 86400000)
              .toISOString()
              .split("T")[0],
            extendedProps: {
              email: s.email,
              details: s.details,
              status: s.status,
              note: s.note
            },
            allDay: true
          }));
          setEvents(mappedEvents);
        }
      })
      .catch(err => console.error("Failed to fetch schedules:", err));
  };

  useEffect(() => {
    fetchRequests();
    fetchSchedules();
  }, []);

  // ================= ADD SCHEDULE =================
  const handleAddSchedule = () => {
    if (!selectedRequestId || !startDate || !endDate) {
      showNotification("Please fill all required fields", "error");
      return;
    }

    fetch("https://api.kemari-corp.com/addSchedule.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        request_id: Number(selectedRequestId),
        start_date: startDate,
        end_date: endDate,
        note: note
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          showNotification("Schedule added successfully!", "success");
          setSelectedRequestId("");
          setStartDate("");
          setEndDate("");
          setNote("");
          setShowAddModal(false);
          fetchSchedules();
        } else {
          showNotification(data.message || "Failed to add schedule", "error");
        }
      })
      .catch(err => {
        console.error("Error adding schedule:", err);
        showNotification("Failed to connect to server", "error");
      });
  };

  // ================= EVENT CLICK → SHOW MODAL =================
  const handleEventClick = (info) => {
    setModalEvent(info.event);
  };

  // ================= EDIT SCHEDULE =================
  const handleEdit = () => {
    if (!modalEvent) return;

    const realEnd = new Date(modalEvent.end);
    realEnd.setDate(realEnd.getDate());

    setEditData({
      scheduleId: modalEvent.id,
      startDate: modalEvent.startStr,
      endDate: realEnd.toISOString().split("T")[0],
      note: modalEvent.extendedProps.note || ""
    });
    setShowEditModal(true);
  };

  const handleUpdateSchedule = () => {
    fetch("https://api.kemari-corp.com/updateSchedule.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        schedule_id: Number(editData.scheduleId),
        start_date: editData.startDate,
        end_date: editData.endDate,
        note: editData.note
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          showNotification("Schedule updated successfully!", "success");
          setShowEditModal(false);
          setModalEvent(null);
          fetchSchedules();
        } else {
          showNotification(data.message || "Failed to update schedule", "error");
        }
      })
      .catch(err => {
        console.error(err);
        showNotification("Failed to update schedule", "error");
      });
  };

  // ================= DELETE SCHEDULE =================
  const handleDelete = () => {
    if (!modalEvent) return;
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    fetch("https://api.kemari-corp.com/deleteSchedule.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ schedule_id: Number(modalEvent.id) })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          showNotification("Schedule deleted successfully!", "success");
          setShowDeleteModal(false);
          setModalEvent(null);
          fetchSchedules();
        } else {
          showNotification(data.message || "Failed to delete schedule", "error");
          setShowDeleteModal(false);
        }
      })
      .catch(err => {
        console.error(err);
        showNotification("Failed to delete schedule", "error");
        setShowDeleteModal(false);
      });
  };

  // ================= DRAG & DROP =================
  const handleEventDrop = (info) => {
    const e = info.event;
    const realEnd = new Date(e.end);
    realEnd.setDate(realEnd.getDate());
    const formattedEnd = realEnd.toISOString().split("T")[0];

    fetch("https://api.kemari-corp.com/updateSchedule.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        schedule_id: Number(e.id),
        start_date: e.startStr,
        end_date: formattedEnd,
        note: e.extendedProps.note || ""
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          showNotification("Schedule moved successfully!", "success");
          fetchSchedules();
        } else {
          showNotification(data.message || "Failed to move schedule", "error");
          info.revert();
        }
      })
      .catch(err => {
        console.error(err);
        showNotification("Failed to move schedule", "error");
        info.revert();
      });
  };

  return (
    <div className="cal-root">

      {/* Page Header */}
      <div className="cal-page-header">
        <h2 className="cal-headline">Service Calendar</h2>
        <button className="cal-add-schedule-btn" onClick={() => setShowAddModal(true)}>
          Add Schedule
        </button>
      </div>

      {/* Calendar */}
      <div className="cal-card">
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={events}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          editable={true}
          height="auto"
        />
      </div>

      {/* Add Schedule Modal */}
      {showAddModal && (
        <div className="cal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="cal-modal cal-add-modal" onClick={e => e.stopPropagation()}>

            <div className="cal-modal-fixed-top">
              <div className="cal-handle" />
              <div className="cal-modal-add-header">
                <h3 className="cal-modal-add-title">Add Schedule</h3>
              </div>
              <div className="cal-modal-divider" />
            </div>

            <div className="cal-modal-scroll">
              <div className="cal-form-grid">
                <div className="cal-field">
                  <label className="cal-label">Request</label>
                  <select
                    className="cal-select"
                    value={selectedRequestId}
                    onChange={e => setSelectedRequestId(e.target.value)}
                  >
                    <option value="">Select a request…</option>
                    {requests.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name} — {r.service} ({r.status})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cal-field">
                  <label className="cal-label">Start Date</label>
                  <input
                    type="date"
                    className="cal-input"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </div>

                <div className="cal-field">
                  <label className="cal-label">End Date</label>
                  <input
                    type="date"
                    className="cal-input"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </div>

                <div className="cal-field cal-field--note">
                  <label className="cal-label">Note</label>
                  <textarea
                    className="cal-textarea"
                    placeholder="Optional note…"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            <div className="cal-modal-fixed-bottom">
              <button className="cal-save-btn" onClick={handleAddSchedule}>
                Add Schedule
              </button>
              <button className="cal-close-btn" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Event Modal */}
      {modalEvent && (
        <div className="cal-overlay" onClick={() => setModalEvent(null)}>
          <div className="cal-modal" onClick={e => e.stopPropagation()}>

            <div className="cal-modal-fixed-top">
              <div className="cal-handle" />
              <div className="cal-modal-top">
                <div className="cal-modal-avatar">
                  {modalEvent.title.charAt(0).toUpperCase()}
                </div>
                <div className="cal-modal-meta">
                  <p className="cal-modal-title">{modalEvent.title}</p>
                  <p className="cal-modal-email">{modalEvent.extendedProps.email}</p>
                  <p className="cal-modal-dates">
                    {modalEvent.startStr}
                    {modalEvent.end && ` → ${new Date(new Date(modalEvent.end).getTime() - 86400000).toISOString().split("T")[0]}`}
                  </p>
                </div>
                <span className={`cal-badge cal-badge--${(modalEvent.extendedProps.status || "new").toLowerCase().replace(/\s/g, "-")}`}>
                  {modalEvent.extendedProps.status || "New"}
                </span>
              </div>
              <div className="cal-modal-divider" />
            </div>

            <div className="cal-modal-scroll">
              {modalEvent.extendedProps.details && (
                <div className="cal-modal-section">
                  <span className="cal-modal-section-label">Details</span>
                  <p className="cal-modal-body">{modalEvent.extendedProps.details}</p>
                </div>
              )}
              {modalEvent.extendedProps.note && (
                <div className="cal-modal-section">
                  <span className="cal-modal-section-label">Note</span>
                  <p className="cal-modal-body">{modalEvent.extendedProps.note}</p>
                </div>
              )}
              {!modalEvent.extendedProps.details && !modalEvent.extendedProps.note && (
                <p className="cal-modal-empty">No details or notes for this schedule.</p>
              )}
            </div>

            <div className="cal-modal-fixed-bottom">
              <div className="cal-modal-actions">
                <button className="cal-action-btn cal-action-btn--edit" onClick={handleEdit}>
                  Edit
                </button>
                <button className="cal-action-btn cal-action-btn--delete" onClick={handleDelete}>
                  Delete
                </button>
              </div>
              <button className="cal-close-btn" onClick={() => setModalEvent(null)}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Edit Schedule Modal */}
      {showEditModal && (
        <div className="cal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="cal-modal cal-add-modal" onClick={e => e.stopPropagation()}>

            <div className="cal-modal-fixed-top">
              <div className="cal-handle" />
              <div className="cal-modal-add-header">
                <h3 className="cal-modal-add-title">Edit Schedule</h3>
              </div>
              <div className="cal-modal-divider" />
            </div>

            <div className="cal-modal-scroll">
              <div className="cal-form-grid">
                <div className="cal-field">
                  <label className="cal-label">Start Date</label>
                  <input
                    type="date"
                    className="cal-input"
                    value={editData.startDate}
                    onChange={e => setEditData({ ...editData, startDate: e.target.value })}
                  />
                </div>

                <div className="cal-field">
                  <label className="cal-label">End Date</label>
                  <input
                    type="date"
                    className="cal-input"
                    value={editData.endDate}
                    onChange={e => setEditData({ ...editData, endDate: e.target.value })}
                  />
                </div>

                <div className="cal-field cal-field--note">
                  <label className="cal-label">Note</label>
                  <textarea
                    className="cal-textarea"
                    placeholder="Optional note…"
                    value={editData.note}
                    onChange={e => setEditData({ ...editData, note: e.target.value })}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            <div className="cal-modal-fixed-bottom">
              <button className="cal-save-btn" onClick={handleUpdateSchedule}>
                Update Schedule
              </button>
              <button className="cal-close-btn" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      {showDeleteModal && (
        <div className="cal-overlay cal-overlay--delete" onClick={() => setShowDeleteModal(false)}>
          <div className="cal-delete-modal" onClick={e => e.stopPropagation()}>

            {/* Icon */}
            <div className="cal-delete-icon-wrap">
              <svg className="cal-delete-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Text */}
            <h3 className="cal-delete-title">Delete Schedule</h3>
            <p className="cal-delete-subtitle">
              Are you sure you want to delete
              {modalEvent && (
                <span className="cal-delete-event-name"> "{modalEvent.title}"</span>
              )}
              ? This action cannot be undone.
            </p>

            {/* Actions */}
            <div className="cal-delete-actions">
              <button className="cal-delete-confirm-btn" onClick={confirmDelete}>
                Delete
              </button>
              <button className="cal-close-btn" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className={`cal-notification cal-notification--${notification.type}`}>
          <div className="cal-notification-content">
            <span className="cal-notification-icon">
              {notification.type === "success" ? "✓" : "✕"}
            </span>
            <span className="cal-notification-message">{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;